import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Flame, Clock, MapPin, RefreshCw, BadgeCheck } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
  'https://images.unsplash.com/photo-1762708546140-7aea0b34ecac?crop=entropy&cs=srgb&fm=jpg&q=85&w=400',
];

const RightNow = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users/right-now`);
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch right now users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    // Refresh every 60 seconds
    const interval = setInterval(fetchUsers, 60000);
    return () => clearInterval(interval);
  }, [fetchUsers]);

  const getPhotoUrl = (user, index = 0) => {
    if (user?.public_photos && user.public_photos.length > 0) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[index % sampleImages.length];
  };

  const formatTimeRemaining = (minutes) => {
    if (minutes <= 0) return 'Expired';
    if (minutes < 60) return `${minutes}m left`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m left`;
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-20">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Flame className="w-6 h-6 text-[#FF2E63]" />
            <h1 className="text-xl font-bold text-[#FF2E63]">Right Now</h1>
          </div>
          <Button
            data-testid="refresh-btn"
            variant="ghost"
            size="icon"
            onClick={fetchUsers}
            disabled={loading}
            className="text-[#A1A1AA] hover:text-white"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="px-4 pb-3 text-sm text-[#A1A1AA]">
          People looking to connect right now
        </p>
      </header>

      {/* Content */}
      <main className="p-4">
        {loading && users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin mb-4" />
            <p className="text-[#A1A1AA]">Finding available users...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Flame className="w-16 h-16 text-[#52525B] mb-4" />
            <h2 className="text-xl font-semibold text-[#EDEDED] mb-2">No one available right now</h2>
            <p className="text-[#A1A1AA] mb-6 max-w-xs">
              Be the first! Set your status to "Available Now" to show up here.
            </p>
            <Button
              data-testid="set-available-btn"
              onClick={() => navigate('/my-profile')}
              className="bg-[#FF2E63] hover:bg-[#E01E50]"
            >
              <Flame className="w-4 h-4 mr-2" />
              Set Available Now
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {users.map((user, index) => (
              <button
                key={user.id}
                data-testid={`right-now-card-${index}`}
                onClick={() => navigate(`/profile/${user.id}`)}
                className="relative rounded-xl overflow-hidden bg-[#1a1a1a] aspect-[3/4] group"
              >
                {/* User Photo */}
                <img
                  src={getPhotoUrl(user, index)}
                  alt={user.display_name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                
                {/* Available Now Badge */}
                <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
                  <div className="flex items-center gap-1 bg-[#FF2E63] text-white text-xs font-bold px-2 py-1 rounded-full">
                    <Flame className="w-3 h-3" />
                    <span>NOW</span>
                  </div>
                  {user.is_verified && (
                    <BadgeCheck className="w-5 h-5 text-[#08D9D6]" />
                  )}
                </div>
                
                {/* Time Remaining */}
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                  <Clock className="w-3 h-3" />
                  <span>{formatTimeRemaining(user.minutes_remaining)}</span>
                </div>
                
                {/* User Info */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-white font-semibold text-sm truncate">
                    {user.display_name}
                    {user.age && <span className="font-normal">, {user.age}</span>}
                  </p>
                  {user.distance !== null && user.distance !== undefined && (
                    <p className="text-[#A1A1AA] text-xs flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {user.distance < 1 ? '< 1' : user.distance} km away
                    </p>
                  )}
                  {user.looking_for && (
                    <p className="text-[#08D9D6] text-xs mt-1 truncate">
                      Looking for: {user.looking_for}
                    </p>
                  )}
                </div>
                
                {/* Online Indicator */}
                {user.is_online && (
                  <div className="absolute top-12 right-2 w-3 h-3 bg-[#08D9D6] rounded-full border-2 border-[#050505]" />
                )}
              </button>
            ))}
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default RightNow;
